import { getLanguage, t } from "@redotech/redo-i18n";
import { type DateSubconditionState } from "@redotech/redo-return-core-common/return-flow/condition-validate";
import { DateSubconditionType } from "@redotech/redo-return-core-common/return-flow/date-subcondition-enums";
import { getDateString } from "@redotech/util/date-format";

export const toModelDateSubcondition = (state: DateSubconditionState) => {
  const result: any = { type: state.type };
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME

  switch (state.type) {
    case DateSubconditionType.ON_OR_BEFORE:
      if (state.beforeDate) {
        result.beforeDate = state.beforeDate.toISOString();
      }
      break;
    case DateSubconditionType.ON_OR_AFTER:
      if (state.afterDate) {
        result.afterDate = state.afterDate.toISOString();
      }
      break;
    case DateSubconditionType.BETWEEN:
      if (state.startDate) {
        result.startDate = state.startDate.toISOString();
      }
      if (state.endDate) {
        result.endDate = state.endDate.toISOString();
      }
      break;
    case DateSubconditionType.LAST_N_DAYS:
      result.days = state.days;
      break;
  }

  return result;
};

// Helper function to provide human-readable standalone description
export const getDateSubconditionDescription = (
  state: DateSubconditionState,
): string => {
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
  switch (state.type) {
    case DateSubconditionType.ON_OR_BEFORE:
      return state.beforeDate
        ? t("On or before {{date}}", {
            date: getDateString(state.beforeDate, { locale: getLanguage() }),
          })
        : t("On or before date");
    case DateSubconditionType.ON_OR_AFTER:
      return state.afterDate
        ? t("On or after {{date}}", {
            date: getDateString(state.afterDate, { locale: getLanguage() }),
          })
        : t("On or after date");
    case DateSubconditionType.BETWEEN:
      return state.startDate && state.endDate
        ? t("Between {{start}} and {{end}}", {
            start: getDateString(state.startDate, { locale: getLanguage() }),
            end: getDateString(state.endDate, { locale: getLanguage() }),
          })
        : t("Between dates");
    case DateSubconditionType.LAST_N_DAYS:
      return state.days
        ? t("Last {{count}} days", { count: +state.days })
        : t("Last n days");
    default:
      return t("All time");
  }
};

// Helper function to get date subcondition description suffix for conditions
export const getDateSubconditionSuffix = (
  state: DateSubconditionState,
): string => {
  // If it's all time, don't add anything
  if (state.type === DateSubconditionType.ALL_TIME) {
    return "";
  }

  // Otherwise, add the appropriate suffix based on the type
  switch (state.type) {
    case DateSubconditionType.LAST_N_DAYS:
      if (state.days) {
        return ` ${t("in the last {{count}} days", { count: +state.days })}`;
      }
      break;
    case DateSubconditionType.ON_OR_AFTER:
      if (state.afterDate) {
        return ` ${t("on or after {{date}}", {
          date: getDateString(state.afterDate, {
            month: "short",
            day: "numeric",
            year: "numeric",
            locale: getLanguage(),
          }),
        })}`;
      }
      break;
    case DateSubconditionType.ON_OR_BEFORE:
      if (state.beforeDate) {
        return ` ${t("on or before {{date}}", {
          date: getDateString(state.beforeDate, {
            month: "short",
            day: "numeric",
            year: "numeric",
            locale: getLanguage(),
          }),
        })}`;
      }
      break;
    case DateSubconditionType.BETWEEN:
      if (state.startDate && state.endDate) {
        return ` ${t("between {{start}} and {{end}}", {
          start: getDateString(state.startDate, {
            month: "short",
            day: "numeric",
            year: "numeric",
            locale: getLanguage(),
          }),
          end: getDateString(state.endDate, {
            month: "short",
            day: "numeric",
            year: "numeric",
            locale: getLanguage(),
          }),
        })}`;
      }
      break;
  }

  return "";
};
